import UserApi from 'api/rest/user'
import { action, createAsyncAction } from 'typesafe-actions'
import { Dispatch } from 'redux'
import {
  AccountDetails,
  AccountDetailsFetchType,
  AccountRegistrationDetails,
  ErrorWithContext,
  FeaturesConfig,
  StoredUser,
  UserStatus,
  WtnConsents,
} from 'types/global'
import api from 'api/graphql/graphql'
import i18n from '../i18n'
import { toast } from 'react-toastify'
import Storage from 'modules/storage'
import { emitAppStart, updateAvatar, uploadAvatar } from 'utils'
import registrationApi from 'api/rest/registration'
import auth from 'modules/auth'
import { withRegLogAsync } from 'modules/reg-logger'
import { SignUpTypeEnum } from 'types/api/enums'

export const SET_LOGGED_IN = 'user/SET_LOGGED_IN'
export const SET_APP_READY = 'user/SET_APP_READY'
export const SET_APP_ERROR = 'user/SET_APP_ERROR'
export const SET_USER_STATUS = 'user/SET_USER_STATUS'
export const SET_TENNIS_ID = 'user/SET_TENNIS_ID'
export const SET_STORED_RAY_ID = 'user/SET_STORED_RAY_ID'
export const RESET_USER = 'user/RESET_USER'
export const SET_IS_ACTIVATING = 'user/SET_IS_ACTIVATING'
export const SET_ACTIVATION_ERROR = 'user/SET_ACTIVATION_ERROR'

export const SET_ACCOUNT_AVATAR_UPDATING = 'profile/SET_ACCOUNT_AVATAR_UPDATING'

export const ACCOUNT_EMAIL_REQUESTED = 'user/ACCOUNT_EMAIL_REQUESTED'
export const ACCOUNT_EMAIL_COMPLETED = 'user/ACCOUNT_EMAIL_COMPLETED'
export const ACCOUNT_EMAIL_FAILED = 'user/ACCOUNT_EMAIL_FAILED'
export const requestAccountEmail = createAsyncAction(
  ACCOUNT_EMAIL_REQUESTED,
  ACCOUNT_EMAIL_COMPLETED,
  ACCOUNT_EMAIL_FAILED
)<void, string, Error>()

export const ACCOUNT_ACTIVATION_REQUESTED = 'user/ACCOUNT_ACTIVATION_REQUESTED'
export const ACCOUNT_ACTIVATION_COMPLETED = 'user/ACCOUNT_ACTIVATION_COMPLETED'
export const ACCOUNT_ACTIVATION_FAILED = 'user/ACCOUNT_ACTIVATION_FAILED'

export const requestAccountActivation = createAsyncAction(
  ACCOUNT_ACTIVATION_REQUESTED,
  ACCOUNT_ACTIVATION_COMPLETED,
  ACCOUNT_ACTIVATION_FAILED
)<void, void, Error>()

export const ACCOUNT_DETAILS_REQUESTED = 'user/ACCOUNT_DETAILS_REQUESTED'
export const ACCOUNT_DETAILS_COMPLETED = 'user/ACCOUNT_DETAILS_COMPLETED'
export const ACCOUNT_DETAILS_FAILED = 'user/ACCOUNT_DETAILS_FAILED'

export const requestAccountDetails = createAsyncAction(
  ACCOUNT_DETAILS_REQUESTED,
  ACCOUNT_DETAILS_COMPLETED,
  ACCOUNT_DETAILS_FAILED
)<void, AccountRegistrationDetails | AccountDetails, Error>()

export const UPDATE_ACCOUNT_DETAILS_REQUESTED = 'user/UPDATE_ACCOUNT_DETAILS_REQUESTED'
export const UPDATE_ACCOUNT_DETAILS_COMPLETED = 'user/UPDATE_ACCOUNT_DETAILS_COMPLETED'
export const UPDATE_ACCOUNT_DETAILS_FAILED = 'user/UPDATE_ACCOUNT_DETAILS_FAILED'

export const updateAccountDetails = createAsyncAction(
  UPDATE_ACCOUNT_DETAILS_REQUESTED,
  UPDATE_ACCOUNT_DETAILS_COMPLETED,
  UPDATE_ACCOUNT_DETAILS_FAILED
)<void, Partial<AccountDetails>, Error>()

export const UPDATE_ACCOUNT_AVATAR_REQUESTED = 'user/UPDATE_ACCOUNT_AVATAR_REQUESTED'
export const UPDATE_ACCOUNT_AVATAR_COMPLETED = 'user/UPDATE_ACCOUNT_AVATAR_COMPLETED'
export const UPDATE_ACCOUNT_AVATAR_FAILED = 'user/UPDATE_ACCOUNT_AVATAR_FAILED'

export const requestUpdateAccountAvatar = createAsyncAction(
  UPDATE_ACCOUNT_AVATAR_REQUESTED,
  UPDATE_ACCOUNT_AVATAR_COMPLETED,
  UPDATE_ACCOUNT_AVATAR_FAILED
)<void, string, Error>()

export const FEATURES_CONFIG_REQUESTED = 'user/FEATURES_CONFIG_REQUESTED'
export const FEATURES_CONFIG_COMPLETED = 'user/FEATURES_CONFIG_COMPLETED'
export const FEATURES_CONFIG_FAILED = 'user/FEATURES_CONFIG_FAILED'
export const requestFeaturesConfig = createAsyncAction(
  FEATURES_CONFIG_REQUESTED,
  FEATURES_CONFIG_COMPLETED,
  FEATURES_CONFIG_FAILED
)<void, FeaturesConfig, Error>()

export const UPDATE_OWN_ACCOUNT_DETAILS = 'user/UPDATE_OWN_ACCOUNT_DETAILS'

export const setLoggedIn = () => action(SET_LOGGED_IN)
export const resetUser = () => action(RESET_USER)
export const setReady = () => action(SET_APP_READY)
export const setAppError = (err: Error) => action(SET_APP_ERROR, err)
export const setUserStatus = (userStatus: UserStatus) => action(SET_USER_STATUS, userStatus)
export const setTennisId = (tennisId: string) => action(SET_TENNIS_ID, tennisId)
export const setStoredRayId = (rayId: string) => action(SET_STORED_RAY_ID, rayId)

export const accountAvatarUpdating = (updating: boolean) =>
  action(SET_ACCOUNT_AVATAR_UPDATING, updating)

export const setAppReady = (user?: StoredUser) => {
  return async (dispatch: Dispatch) => {
    if (user) {
      // flag to make graphQl client authenticate calls
      api.authenticate = true
      dispatch(setLoggedIn())
      dispatch(setUserStatus(user.userStatus))
      dispatch(setTennisId(user.tennisId))
    } else {
      Storage.clearDestinationWatchlistIds()
    }
    dispatch(setReady())
    emitAppStart(user)
  }
}

export const startApp = () => {
  return async dispatch => {
    try {
      // if this call errors out the user has not logged in
      await auth.getAuthToken()
      let userData = await UserApi.getUserData()

      if (userData.userStatus === UserStatus.PENDING && !UserApi.isPendingUserAllowed()) {
        registrationApi.redirectToActivation({ enableLogging: true })
        return
      }

      dispatch(setAppReady(userData))
    } catch (err) {
      let error = err as ErrorWithContext

      if (error.status === 401) {
        console.warn('Unauthenticated user: ', error)
        dispatch(setAppReady())
      } else {
        console.warn('App error: ', error)
        dispatch(setAppError(error))
      }
    }
  }
}

export const logUserOut = () => {
  return async (dispatch: Dispatch) => {
    api.authenticate = false
    Storage.clearAllUserData()
    dispatch(resetUser())
  }
}

export const activateUser = ({
  consents,
  regType,
}: {
  consents: WtnConsents
  regType: SignUpTypeEnum
}) => {
  return async dispatch => {
    const activate = withRegLogAsync(UserApi.activateUser.bind(UserApi), 'activateUser')
    try {
      dispatch(requestAccountActivation.request())

      const res = await activate(consents)

      if (res.success) {
        Storage.saveRegistrationSeed(regType)
        const user = Storage.getUser()
        if (user) {
          user.userStatus = UserStatus.ACTIVE
          Storage.saveUser(user)
        }
        dispatch(requestAccountActivation.success())
      } else {
        throw new Error('User was denied activation')
      }
    } catch (e) {
      console.error(e as Error)
      dispatch(requestAccountActivation.failure(e as Error))
    }
  }
}

export const updateAndActivateUser = ({
  updateInput,
  consents,
  regType,
}: {
  updateInput: Partial<AccountRegistrationDetails>
  consents: WtnConsents
  regType: SignUpTypeEnum
}) => {
  return async dispatch => {
    const updateAccount = withRegLogAsync(
      api.updateAccountDetails.bind(api),
      'updateAccountDetails'
    )
    try {
      dispatch(updateAccountDetails.request())
      const updatedDetails = await updateAccount(updateInput)
      dispatch(updateAccountDetails.success(updatedDetails))
      dispatch(activateUser({ consents, regType }))
    } catch (e) {
      console.warn('Failed to update Person details', e && (e as Error)?.message)
      dispatch(updateAccountDetails.failure(e as Error))
    }
  }
}

export const getAccountEmail = () => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(requestAccountEmail.request())
      const { EmailAddress } = await UserApi.getCurrentUser()
      dispatch(requestAccountEmail.success(EmailAddress || ''))
    } catch (e) {
      console.warn('Failed to fetch account email address: ', e && (e as Error)?.message)
      dispatch(requestAccountEmail.failure(e as Error))
    }
  }
}

export const getAccountDetails = (type: AccountDetailsFetchType) => {
  return async dispatch => {
    const fetchDetails =
      type === AccountDetailsFetchType.REGISTRATION
        ? withRegLogAsync(
            api.getAccountRegistrationDetails.bind(api),
            'getAccountRegistrationDetails'
          )
        : api.getAccountDetails.bind(api)
    try {
      dispatch(requestAccountDetails.request())
      dispatch(requestAccountDetails.success(await fetchDetails()))
    } catch (e) {
      console.warn('Failed to get Person details: ', e && (e as Error)?.message)
      dispatch(requestAccountDetails.failure(e as Error))
    }
  }
}

export const uploadAccountAvatar = file => async dispatch => {
  dispatch(requestUpdateAccountAvatar.request())
  try {
    dispatch(
      requestUpdateAccountAvatar.success(
        await toast.promise(uploadAvatar(file), {
          pending: i18n.t('updatingDetails', { ns: 'strAlerts' }),
          error: i18n.t('detailsNotUpdated', { ns: 'strAlerts' }),
          success: i18n.t('detailsUpdated', { ns: 'strAlerts' }),
        })
      )
    )
  } catch (e) {
    console.warn('Failed to update avatar', e && (e as Error)?.message)
    dispatch(requestUpdateAccountAvatar.failure(e as Error))
  }
}

export const updateAccountAvatar = (avatarUrl: string) => async dispatch => {
  dispatch(requestUpdateAccountAvatar.request())
  try {
    dispatch(
      requestUpdateAccountAvatar.success(
        await toast.promise(updateAvatar(avatarUrl), {
          pending: i18n.t('updatingDetails', { ns: 'strAlerts' }),
          error: i18n.t('detailsNotUpdated', { ns: 'strAlerts' }),
          success: i18n.t('detailsUpdated', { ns: 'strAlerts' }),
        })
      )
    )
  } catch (e) {
    console.warn('Failed to update avatar', e && (e as Error)?.message)
    dispatch(requestUpdateAccountAvatar.failure(e as Error))
  }
}

export const updateOwnAccountDetails =
  (updateInput: Partial<AccountDetails>) => async (dispatch: Dispatch) => {
    try {
      dispatch(updateAccountDetails.request())

      dispatch(
        updateAccountDetails.success(
          await toast.promise(api.updateAccountDetails(updateInput), {
            pending: i18n.t('updatingDetails', { ns: 'strAlerts' }),
            error: i18n.t('detailsNotUpdated', { ns: 'strAlerts' }),
            success: i18n.t('detailsUpdated', { ns: 'strAlerts' }),
          })
        )
      )
    } catch (e) {
      console.warn('Failed to update Person details', e && (e as Error)?.message)
      dispatch(updateAccountDetails.failure(e as Error))
    }
  }

export const getFeaturesConfig = () => {
  return async dispatch => {
    try {
      dispatch(requestFeaturesConfig.request())
      const featuresConfig = await api.getAppFeatures()
      dispatch(requestFeaturesConfig.success(featuresConfig))
    } catch (e) {
      console.warn('Failed to get Features Config: ', e && (e as Error)?.message)
      dispatch(requestFeaturesConfig.failure(e as Error))
    }
  }
}
